import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Shield, Brain, ArrowRight, Umbrella, AlertTriangle } from 'lucide-react';
import SEO, { generateOrganizationSchema, generateVideoSchema } from '../components/SEO';
import AdSense from '../components/AdSense';
import LiteYouTube from '../components/LiteYouTube';

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Options Trading Education Through Clear Analogies | Options Analogies"
        description="Learn options trading through simple analogies. Master cash-secured puts, covered calls, and the wheel strategy with plain-English explanations. Free educational resources."
        keywords="options trading education, learn options trading, options for beginners, cash secured puts, covered calls, wheel strategy, options analogies"
        canonical="https://optionsanalogies.com/"
        type="website"
        schema={[
          generateOrganizationSchema(),
          generateVideoSchema({
            name: "Insurance Core Metaphor Explained",
            description: "Learn how options trading works using the insurance analogy. Understand premiums, strikes, and expiration through a familiar framework.",
            uploadDate: "2025-01-15",
            thumbnailUrl: "https://img.youtube.com/vi/jWtU-goXlDw/maxresdefault.jpg",
            contentUrl: "https://www.youtube.com/watch?v=jWtU-goXlDw"
          })
        ]}
      />
      {/* Hero Section - Ad-Free Zone */}
      <section className="relative overflow-hidden min-h-[600px] flex items-center" data-nosnippet>
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent" />
        
        {/* Floating Geometric Shapes */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Large Circle - Top Right */}
          <div className="absolute top-10 right-10 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}}></div>
          
          {/* Medium Circle - Bottom Left */}
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-cyan-500/15 rounded-full blur-2xl animate-pulse" style={{animationDuration: '5s'}}></div>
          
          {/* Small Accent - Middle Right */}
          <div className="absolute top-1/2 right-1/4 w-40 h-40 bg-teal-400/15 rounded-full blur-xl animate-pulse" style={{animationDuration: '3s'}}></div>
          
          {/* Additional accents */}
          <div className="absolute top-1/3 left-1/3 w-24 h-24 bg-emerald-400/10 rounded-full blur-lg animate-pulse" style={{animationDuration: '6s'}}></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(to right, #10b981 1px, transparent 1px),
                           linear-gradient(to bottom, #10b981 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>

        {/* SVG Illustration - Right Side */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/2 h-full opacity-30 pointer-events-none hidden lg:block">
          <svg viewBox="0 0 400 400" className="w-full h-full">
            {/* Abstract Financial Chart Lines */}
            <path d="M 50 300 Q 100 200 150 250 T 250 200 T 350 150" 
                  stroke="url(#gradient1)" strokeWidth="4" fill="none" opacity="0.8"
                  className="animate-pulse" style={{animationDuration: '3s'}} />
            <path d="M 50 250 Q 100 180 150 220 T 250 180 T 350 130" 
                  stroke="url(#gradient2)" strokeWidth="3" fill="none" opacity="0.6"
                  className="animate-pulse" style={{animationDuration: '4s'}} />
            <path d="M 50 200 Q 100 150 150 180 T 250 140 T 350 100" 
                  stroke="url(#gradient3)" strokeWidth="2" fill="none" opacity="0.4"
                  className="animate-pulse" style={{animationDuration: '5s'}} />
            
            {/* Circular Elements - Data Points */}
            <circle cx="100" cy="250" r="10" fill="#10b981" opacity="0.8" className="animate-pulse" />
            <circle cx="200" cy="200" r="10" fill="#14b8a6" opacity="0.8" className="animate-pulse" style={{animationDuration: '2s'}} />
            <circle cx="300" cy="150" r="10" fill="#06b6d4" opacity="0.8" className="animate-pulse" style={{animationDuration: '3s'}} />
            
            {/* Additional accent circles */}
            <circle cx="150" cy="220" r="6" fill="#10b981" opacity="0.5" />
            <circle cx="250" cy="180" r="6" fill="#14b8a6" opacity="0.5" />
            
            {/* Abstract shapes representing options/analytics */}
            <rect x="80" y="320" width="40" height="60" rx="4" fill="url(#gradient1)" opacity="0.3" />
            <rect x="180" y="280" width="40" height="100" rx="4" fill="url(#gradient2)" opacity="0.3" />
            <rect x="280" y="240" width="40" height="140" rx="4" fill="url(#gradient3)" opacity="0.3" />
            
            {/* Gradient Definitions */}
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0.2" />
              </linearGradient>
              <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.1" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-3xl animate-fade-in-up">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Understand options through analogies.
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              We translate CSPs, covered calls, and the Wheel into plain-English mental models—so the mechanics finally click.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" asChild>
                <Link to="/csp">Start with CSP</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/analogies">Browse Analogies</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Core Metaphor - Ad-Free Zone */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-nosnippet>
        <div className="glass rounded-2xl p-8 md:p-12">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The "Insurance" Core Metaphor
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Think of yourself as the insurance company. When you sell options, you're underwriting policies.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Left Column - Video */}
            <div className="w-full">
              <div className="rounded-lg overflow-hidden border border-emerald-500/20 shadow-lg bg-gray-900">
                <LiteYouTube 
                  videoId="jWtU-goXlDw"
                  title="Insurance Core Metaphor Explained"
                />
                <div className="bg-gray-800/50 px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-400">Watch: Insurance Core Metaphor</span>
                  <a 
                    href="https://youtu.be/jWtU-goXlDw" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                  >
                    <span>Watch on YouTube</span>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Critical Understanding & Key Mappings */}
            <div className="flex flex-col gap-6">
              {/* Critical Understanding Box */}
              <div className="risk-callout p-6 rounded-lg border border-red-500/30 bg-red-500/5">
                <h3 className="text-xl font-bold text-red-400 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Critical Understanding
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Premium ≠ free lunch. You accept defined obligations. Insurance companies profit over time through careful underwriting—not by collecting premiums blindly. The same discipline applies to options.
                </p>
              </div>

              {/* Key Mappings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-emerald-400 mb-3">Key Mappings</h3>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/30">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Premium = Cash Now</p>
                    <p className="text-gray-400 text-sm">Collect upfront payment for taking on risk</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/30">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Strike = Coverage Level</p>
                    <p className="text-gray-400 text-sm">The price at which you must perform</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/30">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Expiration = Policy Term</p>
                    <p className="text-gray-400 text-sm">How long your obligation lasts</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/30">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">ITM = Claim Filed</p>
                    <p className="text-gray-400 text-sm">When you must pay out (assignment)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Placement 1 - After Insurance Metaphor */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdSense slot="7273008442" format="horizontal" />
      </section>

      {/* Why Analogies */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why analogies?</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Complex concepts become clear when mapped to familiar patterns
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="bg-gray-900/50 border-gray-800 card-hover">
            <CardHeader>
              <Brain className="w-12 h-12 text-emerald-500 mb-4" />
              <CardTitle className="text-white">Reduce Cognitive Load</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">
                Transform abstract options mechanics into concrete, relatable scenarios your brain already understands.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800 card-hover">
            <CardHeader>
              <Shield className="w-12 h-12 text-emerald-500 mb-4" />
              <CardTitle className="text-white">Map to Familiar Contracts</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">
                Options are just specialized contracts. See how they mirror insurance, warranties, and real estate agreements.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800 card-hover">
            <CardHeader>
              <Umbrella className="w-12 h-12 text-emerald-500 mb-4" />
              <CardTitle className="text-white">Remember the Risks</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">
                Every analogy includes the downsides. Understand obligations, assignment scenarios, and when strategies fail.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What you'll learn</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link to="/csp" className="group">
            <Card className="bg-gray-900/50 border-gray-800 h-full card-hover">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  Cash-Secured Puts
                  <ArrowRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-1 transition" />
                </CardTitle>
                <CardDescription>
                  A paid limit order: get paid now to promise you'll buy later at your price.
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/covered-calls" className="group">
            <Card className="bg-gray-900/50 border-gray-800 h-full card-hover">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  Covered Calls
                  <ArrowRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-1 transition" />
                </CardTitle>
                <CardDescription>
                  A paid listing agreement: get paid now to agree you'll sell at your price.
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/wheel" className="group">
            <Card className="bg-gray-900/50 border-gray-800 h-full card-hover">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  The Wheel Strategy
                  <ArrowRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-1 transition" />
                </CardTitle>
                <CardDescription>
                  Run a shop: get paid to stock (CSP), then get paid to sell (CC).
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/glossary" className="group">
            <Card className="bg-gray-900/50 border-gray-800 h-full card-hover">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  Glossary
                  <ArrowRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-1 transition" />
                </CardTitle>
                <CardDescription>
                  Core options terminology explained in plain English.
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/analogies" className="group">
            <Card className="bg-gray-900/50 border-gray-800 h-full card-hover">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  Analogies Library
                  <ArrowRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-1 transition" />
                </CardTitle>
                <CardDescription>
                  Browse all analogies in one place with stories and formulas.
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/faqs" className="group">
            <Card className="bg-gray-900/50 border-gray-800 h-full card-hover">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  FAQs
                  <ArrowRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-1 transition" />
                </CardTitle>
                <CardDescription>
                  Common questions about options, risks, and strategies.
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        </div>
      </section>

      {/* Ad Placement 2 - Before Footer */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdSense slot="8131612785" format="rectangle" />
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="glass rounded-2xl p-8 md:p-12 text-center">
          <BookOpen className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to see options differently?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Access our comprehensive learning kits with one-pagers, slide decks, and worksheets.
          </p>
          <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" asChild>
            <Link to="/teaching-kits">Get Learner's Kits</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;